import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BotSandbox } from '@/components/onboarding/sandbox/BotSandbox';
import type { SectionEditProps } from '@/components/onboarding/OnboardingTimeline';
import { PreviewVerifyEmailCard } from '@/components/onboarding/PreviewVerifyEmailCard';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useOnboardingState } from '@/contexts/OnboardingContext';
import { previewChatService } from '@/services/previewChatService';

export function PreviewSection({ editing = false }: SectionEditProps = {}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { profile, markStepCompleted } = useOnboardingState();

  const [messagesUsed, setMessagesUsed] = useState(0);
  const [cap, setCap] = useState(20);
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
  const [advancing, setAdvancing] = useState(false);

  const businessName = profile?.business_name || user?.name || '';

  useEffect(() => {
    let active = true;
    previewChatService
      .getState()
      .then((s) => {
        if (!active) return;
        setMessagesUsed(s.messages_used);
        setCap(s.cap);
        setEmailVerified(s.email_verified);
      })
      .catch(() => {
        if (active) setEmailVerified(true);
      });
    return () => {
      active = false;
    };
  }, []);

  function handleMessagesUpdate(used: number, newCap: number) {
    setMessagesUsed(used);
    setCap(newCap);
  }

  async function handleAdvance() {
    setAdvancing(true);
    try {
      await markStepCompleted('preview');
    } finally {
      setAdvancing(false);
    }
  }

  if (emailVerified === false) {
    return (
      <div data-testid="section-preview">
        <PreviewVerifyEmailCard onVerified={() => setEmailVerified(true)} />
      </div>
    );
  }

  return (
    <div data-testid="section-preview" className="space-y-4">
      {/* IG-mimic surface — scoped tokens apply only inside BotSandbox.
          BotSandbox owns the header (Avatar + business name) so EnrichmentSurface
          also gets the full IG-mimic chrome without duplication. */}
      <div className="mx-auto w-full max-w-[28rem]">
        <div className="overflow-hidden rounded-2xl border border-[color:var(--preview-chat-header-border)] bg-[color:var(--preview-chat-bg)]">
          <BotSandbox businessName={businessName} onMessagesUpdate={handleMessagesUpdate} />
        </div>
      </div>

      {/* Counter + primary advance. Single button — Edit-KB lives on the KB node now.
          F063: in edit mode there is nothing to save — re-testing is the point;
          the advance CTA is hidden (exit via the node's Cancelar) so the funnel
          pointer is never touched. */}
      <div className="mx-auto flex w-full max-w-[28rem] flex-wrap items-center justify-between gap-3">
        <span className="min-w-0 text-sm text-muted-foreground" data-testid="preview-counter">
          {t('onboarding.preview.counter', { used: messagesUsed, cap })}
        </span>
        {!editing && (
          <Button
            onClick={handleAdvance}
            disabled={messagesUsed === 0 || advancing}
            className="min-h-11 shrink-0"
          >
            {t('onboarding.preview.advance')}
          </Button>
        )}
      </div>

      {/* F069 #13 — the bare counter needs an explanation of what the quota is */}
      <p
        className="mx-auto w-full max-w-[28rem] text-xs text-muted-foreground"
        data-testid="preview-quota-hint"
      >
        {t('onboarding.preview.quota_hint', { cap })}
      </p>
    </div>
  );
}
